import { produce } from 'immer';

import NormalizedObjects from '@libs/redux/NormalizedObjects';

import {
  BULK_REQUEST_REQUEST_TASKS_STATUS__FETCH_SUCCESS,
  BULK_REQUEST_REQUEST_TASKS_TASKS__FETCH_FAILURE,
  BULK_REQUEST_REQUEST_TASKS_TASKS__FETCH_REQUEST,
  BULK_REQUEST_REQUEST_TASKS_TASKS__FETCH_SUCCESS,
  BULK_REQUEST_REQUEST_TASKS_TASKS__PUT_FAILURE,
  BULK_REQUEST_REQUEST_TASKS_TASKS__PUT_REQUEST,
  BULK_REQUEST_REQUEST_TASKS_TASKS__PUT_SUCCESS,
  BULK_REQUEST_REQUEST_TASKS_TASKS__RELOAD_FAILURE,
  BULK_REQUEST_REQUEST_TASKS_TASKS__RELOAD_SUCCESS,
} from '@app/store/actionTypes';
import { StatusAction } from '@app/store/bulkRequest/requestTasks/status/actions';
import { TasksAction } from '@app/store/bulkRequest/requestTasks/tasks/actions';
import RequestTask from '@app/store/bulkRequest/requestTasks/tasks/RequestTask';
import RequestTaskStatus from '@app/store/bulkRequest/requestTasks/tasks/RequestTaskStatus';
import RequestTasksResponse from '@app/store/transport-layer/bulk-request/bulk-requests/request-tasks/RequestTasksResponse';

export interface TasksState extends NormalizedObjects<RequestTask> {
  count: number | null;
  isFetching: boolean;
  isUpdating: boolean;
  page: number | null;
  totalCount: number | null;
}

const initialState: TasksState = {
  allIds: [],
  byId: {},
  count: null,
  isFetching: false,
  isUpdating: false,
  page: null,
  totalCount: null,
};

export default function reducer(
  state: TasksState = initialState,
  action: StatusAction | TasksAction
) {
  switch (action.type) {
    case BULK_REQUEST_REQUEST_TASKS_STATUS__FETCH_SUCCESS:
      return produce(state, (draft) => {
        const {
          canceled,
          cancelingFailed,
          cancelingReady,
          failed,
          pause,
          pending,
          ready,
          success,
        } = action.payload.status;
        const { byId, allIds } = draft;
        canceled.ids.forEach((id: string) => {
          if (allIds.includes(id)) {
            byId[id].status = RequestTaskStatus.CANCELED;
          }
        });
        cancelingFailed.ids.forEach((id: string) => {
          if (allIds.includes(id)) {
            byId[id].status = RequestTaskStatus.CANCELING_FAILED;
          }
        });
        cancelingReady.ids.forEach((id: string) => {
          if (allIds.includes(id)) {
            byId[id].status = RequestTaskStatus.CANCELING_READY;
          }
        });
        failed.ids.forEach((id: string) => {
          if (allIds.includes(id)) {
            byId[id].status = RequestTaskStatus.FAILED;
          }
        });
        pause.ids.forEach((id: string) => {
          if (allIds.includes(id)) {
            byId[id].status = RequestTaskStatus.PAUSE;
          }
        });
        pending.ids.forEach((id: string) => {
          if (allIds.includes(id)) {
            byId[id].status = RequestTaskStatus.PENDING;
          }
        });
        ready.ids.forEach((id: string) => {
          if (allIds.includes(id)) {
            byId[id].status = RequestTaskStatus.READY;
          }
        });
        success.ids.forEach((id: string) => {
          if (allIds.includes(id)) {
            byId[id].status = RequestTaskStatus.SUCCESS;
          }
        });
      });
    case BULK_REQUEST_REQUEST_TASKS_TASKS__FETCH_REQUEST:
      return produce(state, (draft) => {
        const { page } = action.payload;
        draft.isFetching = true;
        if (page === 1) {
          draft.allIds = [];
          draft.byId = {};
          draft.count = null;
          draft.page = null;
          draft.totalCount = null;
        }
      });
    case BULK_REQUEST_REQUEST_TASKS_TASKS__FETCH_SUCCESS:
      return produce(state, (draft) => {
        const { totalCount, count, page, data } = action.payload as RequestTasksResponse;
        draft.totalCount = totalCount;
        draft.count = count;
        draft.isFetching = false;
        draft.page = page;

        const { byId, allIds } = draft;
        data.forEach((requestTask: RequestTask) => {
          byId[requestTask.id] = requestTask;
          if (!allIds.includes(requestTask.id)) {
            allIds.push(requestTask.id);
          }
        });
      });
    case BULK_REQUEST_REQUEST_TASKS_TASKS__RELOAD_SUCCESS:
      return produce(state, (draft) => {
        const { data } = action.payload as RequestTasksResponse;
        draft.isFetching = false;
        /**
         * 이 액션은 기존 조회한 작업 목록을 갱신하는 것이라서 기존 조회한
         * page, count, totalCount는 갱신하지 않습니다.
         */
        const { byId, allIds } = draft;
        data.forEach((requestTask: RequestTask) => {
          byId[requestTask.id] = requestTask;
          if (!allIds.includes(requestTask.id)) {
            allIds.push(requestTask.id);
          }
        });
      });
    case BULK_REQUEST_REQUEST_TASKS_TASKS__PUT_REQUEST:
      return produce(state, (draft) => {
        draft.isUpdating = true;
      });
    case BULK_REQUEST_REQUEST_TASKS_TASKS__PUT_FAILURE:
    case BULK_REQUEST_REQUEST_TASKS_TASKS__PUT_SUCCESS:
      return produce(state, (draft) => {
        draft.isUpdating = false;
      });
    case BULK_REQUEST_REQUEST_TASKS_TASKS__FETCH_FAILURE:
    case BULK_REQUEST_REQUEST_TASKS_TASKS__RELOAD_FAILURE:
      return produce(state, (draft) => {
        draft.isFetching = false;
      });
    default:
      return state;
  }
}
